import type * as Ably from 'ably';

import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

/**
 * Mints an Ably TokenRequest from the public API.
 *
 * This is the only place the website asks for realtime credentials. The live
 * match engine also issues tokens, but it never verified the bearer token it
 * was handed: the raw string became the Ably client identity, so any string was
 * accepted. The public API verifies the access token first and derives the
 * identity from the profile that subject owns, which is why both the live pitch
 * and the transfers doorbell mint here (FFH-2225).
 *
 * One connection carries one token, so a single capability map has to cover
 * every feed a client reads. The issued token grants the whole `match:` and
 * `scores:` namespaces plus the caller's own notification channel, so the live
 * pitch and the doorbell can share this call without either narrowing what the
 * other receives.
 */
/**
 * Suffix describing why a mint failed, appended to the leading message rather
 * than replacing it so callers and tests can still match on the message alone.
 */
const describeFailure = (
  response: Response | undefined,
  error: unknown
): string => {
  const status = response?.status;
  const message =
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as { error?: { message?: unknown } }).error?.message ===
      'string'
      ? (error as { error: { message: string } }).error.message
      : undefined;

  if (status && message) return ` (${status}: ${message})`;
  if (status) return ` (${status})`;
  return '';
};

export const fetchAblyTokenRequest = async (
  accessToken: string,
  matchIds: string[] = []
): Promise<Ably.TokenRequest> => {
  // An empty array is dropped rather than sent: the contract requires a
  // non-empty list when the field is present, and absent means the same thing
  // here anyway. `matchIds` no longer narrows the issued capability, but it
  // stays in the request because the endpoint is expected to grow a
  // per-entitlement capability model, at which point it will.
  const body = matchIds.length > 0 ? { matchIds } : {};

  const { data, error, response } = await ffhClient().POST('/ably/token', {
    headers: authHeader(accessToken),
    body
  });

  if (error || !data) {
    // Carry the status and the backend's own message. The failures this
    // endpoint distinguishes need telling apart from the console: 429 means
    // the caller burned its mint budget, 503 means realtime is unconfigured in
    // this environment, 401 means the access token did not verify. Collapsing
    // them into one string makes a live-match outage guesswork.
    throw new Error(
      `Failed to mint a realtime token${describeFailure(response, error)}`
    );
  }

  return data.data as Ably.TokenRequest;
};
